import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import i18n from '@/i18n/translations';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { RootState } from '@/redux/store';
import { IResRatesData, IResRatesOK } from '@/types/parcel/userData/rates.interface';
import { handleTranslations } from '@/utils/common';

import { generateTableObject, setHeaders, Table } from './dependencies/helpers';

interface Rates {
  loading: boolean;
  error: string | null;
  data: IResRatesData | null;
}

const useRates = () => {
  const [table, setTable] = useState<Table[] | []>([]);
  const [rates, setRates] = useState<Rates>({
    loading: false,
    error: null,
    data: null,
  });
  const { profile } = useSelector((state: RootState) => state.parcel);
  const country = useMemo(() => profile.data?.country, [profile]);
  const { t } = useTranslation('common');
  const getRates = async () => {
    const http = initializeHttpProvider();
    try {
      setRates((prev) => ({ ...prev, loading: true }));

      const response = await http.userData.rates();
      setRates((prev) => ({
        ...prev,
        error: null,
        data: (response as IResRatesOK).data,
      }));
    } catch (e: any) {
      setRates((prev) => ({
        ...prev,
        error: e.message ?? 'Error al obtener las tarifas',
        data: null,
      }));
    } finally {
      setRates((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    const translation = handleTranslations(profile.data?.country ?? 'MEX');
    i18n
      .changeLanguage(translation)
      .then((newt) => {
        newt('currencySymbol');
        newt('currencyNomenclature');
      })
      .catch(() => {});
    void getRates();
  }, [profile.data?.country]);

  useEffect(() => {
    return setTable(
      generateTableObject(
        setHeaders(country ?? 'MEX'),
        rates.data?.rates ?? [],
        rates.data?.foreignRates ?? {},
        country!,
        t,
      ),
    );
  }, [rates, country, t]);

  return {
    table,
    country,
    rates,
  };
};

export default useRates;
